import React, {useEffect, useState} from "react";
import {Accordion, Button, ButtonGroup, Col, Form, Row} from "react-bootstrap";
import HelpTooltip from "../HelpTooltip.jsx";
import {computeRackLayout} from "./LagerplaetzeLayout.js";
import {formatNumber} from "./format.js";

// Die beiden gängigen Stapler-Arbeitsgangbreiten aus dem Auftrag - als
// Schnellauswahl neben dem frei editierbaren Feld, keine native <select>.
const AISLE_PRESETS = [
    {label: "Schubmaststapler (3,2 m)", value: 3.2},
    {label: "Gegengewichtsstapler (4,0 m)", value: 4.0},
];

/**
 * Wandelt eine rohe Texteingabe (Komma oder Punkt als Dezimaltrennzeichen)
 * in eine gültige, positive Zahl - eine ungültige Eingabe fällt auf den
 * zuletzt gültigen Wert zurück, statt eine native Formularfehlermeldung
 * auszulösen (siehe globale Vorgabe "keine browsereigenen Bedienelemente").
 */
function parsePositiveNumber(raw, fallback) {
    const parsed = parseFloat(String(raw).replace(",", "."));
    if (Number.isNaN(parsed) || parsed <= 0) {
        return fallback;
    }
    return parsed;
}

/**
 * Einzelnes Zahlenfeld mit kurzer Schreibpause, bevor die Eingabe committet
 * wird - dasselbe Muster wie in InventoryStrategyAssumptions.jsx, damit sich
 * Zahlenfelder im Haus überall gleich verhalten.
 */
function NumberField({value, onCommit, integer = false, step = "1"}) {
    const [input, setInput] = useState(String(value));

    useEffect(() => {
        setInput(String(value));
    }, [value]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            const parsed = parsePositiveNumber(input, value);
            const finalValue = integer ? Math.max(1, Math.round(parsed)) : Math.round(parsed * 100) / 100;
            if (finalValue !== value) {
                onCommit(finalValue);
            }
        }, 400);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [input]);

    return (
        <Form.Control
            type="number"
            size="sm"
            min={integer ? 1 : 0.1}
            step={step}
            value={input}
            onChange={(event) => setInput(event.target.value)}
        />
    );
}

/**
 * Aufklappbare Leiste mit den änderbaren Maßen und Regalannahmen der drei
 * Hallen (Auftrag Punkt "Die Maße"): Hallenmaße, Stellplatzbreite,
 * Regaltiefe, Arbeitsgang, Quergang und Regalebenen. Die Palettenkapazität
 * ist kein eigenes Feld mehr, sondern ein errechnetes Ergebnis (siehe
 * computeRackLayout in LagerplaetzeLayout.js) und wird hier nur noch
 * angezeigt. Der Zustand liegt bei der aufrufenden Seite
 * (Pages/Statistics/Lagerplaetze), jede Änderung wirkt sofort auf Grafik und
 * Kennzahlen.
 */
export default function LagerplaetzeWarehouseSettings({warehouses, onChangeField}) {
    return (
        <Accordion>
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    <div className="d-flex flex-column">
                        <span className="fw-semibold">Hallenmaße und Regalannahmen</span>
                        <span className="small text-muted">Annäherungswerte - hier anpassen, sobald belastbare Zahlen vorliegen</span>
                    </div>
                </Accordion.Header>
                <Accordion.Body>
                    {warehouses.map((warehouse, index) => {
                        const layout = computeRackLayout(warehouse);
                        return (
                            <Row
                                key={warehouse.id}
                                className={`g-3 align-items-end${index < warehouses.length - 1 ? " mb-3 pb-3 border-bottom" : ""}`}
                            >
                                <Col xs={12} className="d-flex justify-content-between align-items-baseline flex-wrap gap-2">
                                    <div className="fw-semibold">{warehouse.name}</div>
                                    <div className="small text-muted">
                                        {warehouse.capacityFile ? (
                                            <>Gezählte Kapazität laut Kapazitätsdatei: <strong>{formatNumber(warehouse.capacityFile.kapazitaet)} Palettenplätze</strong> - die Regalannahmen hier ändern nur die Zeichnung (Rechenmodell: {formatNumber(layout.totalCapacity)})</>
                                        ) : (
                                            <>Errechnete Kapazität: <strong>{formatNumber(layout.totalCapacity)} Palettenplätze</strong></>
                                        )}
                                    </div>
                                </Col>
                                <Col md={3} sm={6}>
                                    <Form.Label className="small text-muted mb-1">Länge (m)</Form.Label>
                                    <NumberField value={warehouse.length} onCommit={(v) => onChangeField(warehouse.id, "length", v)} />
                                </Col>
                                <Col md={3} sm={6}>
                                    <Form.Label className="small text-muted mb-1">Breite (m)</Form.Label>
                                    <NumberField value={warehouse.width} onCommit={(v) => onChangeField(warehouse.id, "width", v)} />
                                </Col>
                                <Col md={3} sm={6}>
                                    <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                        Stellplatzbreite (m)
                                        <HelpTooltip placement="right" size={0.7}>
                                            Breite eines Palettenstellplatzes längs eingelagert (Europalette 0,8 m).
                                        </HelpTooltip>
                                    </Form.Label>
                                    <NumberField
                                        value={warehouse.palletWidth}
                                        step="0.1"
                                        onCommit={(v) => onChangeField(warehouse.id, "palletWidth", v)}
                                    />
                                </Col>
                                <Col md={3} sm={6}>
                                    <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                        Regaltiefe (m)
                                        <HelpTooltip placement="right" size={0.7}>
                                            Tiefe einer einzelnen Regalreihe. Zwei Reihen Rücken an Rücken (Doppelregal)
                                            ergeben die doppelte Tiefe.
                                        </HelpTooltip>
                                    </Form.Label>
                                    <NumberField
                                        value={warehouse.rackDepth}
                                        step="0.1"
                                        onCommit={(v) => onChangeField(warehouse.id, "rackDepth", v)}
                                    />
                                </Col>
                                <Col md={4} sm={6}>
                                    <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                        Arbeitsgang zwischen Regalmodulen (m)
                                        <HelpTooltip placement="right" size={0.7}>
                                            Fahrgasse für den Stapler zwischen zwei Doppelregalen. Schubmaststapler
                                            brauchen rund 3,2 m, Gegengewichtsstapler rund 4,0 m.
                                        </HelpTooltip>
                                    </Form.Label>
                                    <div className="d-flex flex-column gap-1">
                                        <NumberField
                                            value={warehouse.aisleWidth}
                                            step="0.1"
                                            onCommit={(v) => onChangeField(warehouse.id, "aisleWidth", v)}
                                        />
                                        <ButtonGroup size="sm">
                                            {AISLE_PRESETS.map((preset) => (
                                                <Button
                                                    key={preset.value}
                                                    type="button"
                                                    variant={warehouse.aisleWidth === preset.value ? "primary" : "outline-secondary"}
                                                    onClick={() => onChangeField(warehouse.id, "aisleWidth", preset.value)}
                                                >
                                                    {preset.label}
                                                </Button>
                                            ))}
                                        </ButtonGroup>
                                    </div>
                                </Col>
                                <Col md={4} sm={6}>
                                    <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                        Quergang je Hallenende (m)
                                        <HelpTooltip placement="right" size={0.7}>
                                            Querweg an beiden Hallenenden, quer zu den Regalreihen. Der erste dient
                                            zugleich als Zone für Wareneingang/-ausgang.
                                        </HelpTooltip>
                                    </Form.Label>
                                    <NumberField
                                        value={warehouse.endAisle}
                                        step="0.1"
                                        onCommit={(v) => onChangeField(warehouse.id, "endAisle", v)}
                                    />
                                </Col>
                                <Col md={4} sm={6}>
                                    <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                        Regalebenen
                                        <HelpTooltip placement="right" size={0.7}>
                                            Anzahl übereinanderliegender Ebenen je Stellplatz - multipliziert die
                                            Kapazität der Grundfläche.
                                        </HelpTooltip>
                                    </Form.Label>
                                    <NumberField
                                        value={warehouse.levels}
                                        integer
                                        onCommit={(v) => onChangeField(warehouse.id, "levels", v)}
                                    />
                                </Col>
                                {warehouse.note && <Col xs={12} className="small text-muted">{warehouse.note}</Col>}
                            </Row>
                        );
                    })}
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}
